import { useEffect, useRef, useState } from "react";

import { useTranslation } from "react-i18next";
import ReactLoading from "react-loading";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";

import { usePermissions } from "hooks/permission";
import { useToast } from "hooks/toast";
import { useMediaPermissions } from "hooks/useMediaPermissions";

import { setScreenStatus } from "store/modules/screenControl/actions";
import { setTimeToCount } from "store/modules/user/actions";
import { useAppSelector } from "store/rootReducer";

import { onLoadList } from "features/ImportCertificate/constants";

import ModalDefault from "components/ModalDefault";
import SearchEmpty from "components/SearchEmpty";

import { HearingService } from "services/hearing";

import RecordButtonDisabled from "assets/svg/record-button-disabled.svg";
import RecordButton from "assets/svg/record-button.svg";
import Share from "assets/svg/share.svg";
import V from "assets/svg/v.svg";

import { FORMS_HEARING } from "utils/entities/permission";
import { useQuery } from "utils/get";

import {
  BodyContainer,
  ButtonFinish,
  Container,
  ContainerButtons,
  ContainerDescription,
  FooterContainer,
  HeaderContainer,
  Subtitle,
  Title,
} from "./style";

export default function HearingRecord() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { addToast } = useToast();
  const [loadingStopRecording, setLoadingStopRecording] = useState(false);
  const [showModalConfirmation, setShowModalConfirmation] = useState(false);
  const [loadingStartRecording, setLoadingStartRecording] = useState(true);
  const [disabledButtons, setDisabledButtons] = useState(true);
  const [showModalConfirmationSair, setShowModalConfirmationSair] =
    useState(false);
  const [exibirSaindoComponent, setExibirSaindoComponent] = useState(false);
  const [recordStarted, setRecordStarted] = useState(false);
  const timeoutsRef = useRef<any[]>([]);
  const dispatch = useDispatch();
  const query = useQuery();
  const permissions = useMediaPermissions();

  const { create } = usePermissions(FORMS_HEARING.HEARING);
  const { timeToCount } = useAppSelector((state) => state.user);
  const { user } = useAppSelector((state) => state.user);
  const { globalConfigurations } = useAppSelector(
    (state) => state.configurations,
  );

  function setTimeToEndRecord(tempo: number) {
    dispatch(setTimeToCount(tempo));
  }

  async function getTimeOfRecordForUser(customerId: number) {
    const response = await onLoadList(customerId);
    if (response.data && response.data.certificateType === 2) {
      setTimeToEndRecord(response.data.limitRecord);
    }
  }

  useEffect(() => {
    getTimeOfRecordForUser(user?.customerId as number);
  }, []);

  const checkRecordingStatus = async () => {
    try {
      const response = await HearingService.getUserInRoom(
        location.state?.videoRoomId,
      );
      if (response.body.ok) {
        setRecordStarted(true);
        addToast({
          type: "warning",
          title: t("recording.inProgress"),
          description: t("recording.inProgressDescription"),
        });
      } else {
        setTimeout(() => {
          setLoadingStartRecording(false);
        }, 5000);
        if (create) {
          addToast({
            type: "warning",
            title: t("recording.reminderTitle"),
            description: t("recording.reminderDescription"),
          });
        }
        setRecordStarted(false);
        localStorage.removeItem("isRecordStarted");
      }
    } catch (error) {
      console.error("Erro ao verificar status da gravação:", error);
      addToast({
        type: "error",
        title: t("recording.verifyError"),
        description: t("recording.verifyErrorDescription"),
      });
    } finally {
      setLoadingStartRecording(false);
    }
  };

  useEffect(() => {
    if (!location.state) {
      navigate("/audiencias");
      return;
    }
    checkRecordingStatus();
    dispatch(setScreenStatus(true));
    return () => {
      timeoutsRef.current.forEach((timeout) => {
        clearTimeout(timeout);
      });
    };
  }, []);

  const goBack = () => {
    dispatch(setScreenStatus(false));
  };

  const copyFunction = async () => {
    const copyText: any = document.getElementById("myInput");

    if (copyText) {
      copyText.select();
      copyText.setSelectionRange(0, 99999); // For mobile devices

      await navigator.clipboard.writeText(copyText.value);
      addToast({
        type: "success",
        title: t("recording.linkCopied"),
        description: t("recording.linkCopiedDescription"),
      });
    }
  };

  const startVideoRecord = async () => {
    const existsCamPermission = await requestPermission();
    if (!existsCamPermission) {
      addToast({
        type: "warning",
        title: t("recording.cameraRequired"),
        description: t("recording.cameraRequiredDescription"),
      });
      return;
    }

    if (location.state) {
      setDisabledButtons(true);
      setLoadingStartRecording(true);
      localStorage.setItem("isRecordStarted", "true");
      const { status } = await HearingService.startVideoRecording(
        location.state?.videoRoomId,
        location.state.authToken,
        location.state.deviceToken,
      );

      if (status) {
        if (!timeToCount) {
          addToast({
            type: "success",
            title: t("recording.started"),
            description: t("recording.startedDescription"),
          });
        }

        setLoadingStartRecording(false);
        setRecordStarted(true);
        verificarEChamarlimiteDeTempo();
      } else {
        addToast({
          type: "error",
          title: t("recording.startError"),
          description: t("recording.startErrorDescription"),
        });
        setLoadingStartRecording(false);
        setRecordStarted(false);
        setDisabledButtons(false);
      }
    }
  };

  const stopVideoRecord = async () => {
    if (location.state) {
      setLoadingStopRecording(true);
      const { status } = await HearingService.stopVideoRecording(
        location.state?.videoRoomId,
        location.state.authToken,
        location.state.deviceToken,
      );

      if (status) {
        setExibirSaindoComponent(true);

        const response = await HearingService.deleteRoom(
          location.state?.videoRoomId,
          location.state.authToken,
          location.state.deviceToken,
        );

        if (response.status) {
          setTimeout(() => {
            addToast({
              type: "success",
              title: t("recording.videoFinished"),
              description: t("recording.videoFinishedDescription"),
            });
            setLoadingStopRecording(false);
          }, 3000);
          // await HearingService.logoutUna();
          navigate(
            `/audiences?hearingId=${query.get(
              "hearingId",
            )}&hearingFileId=${query.get("hearingFileId")}`,
            {
              replace: true,
              state: {
                procedureNumber: location.state?.procedureNumber,
              },
            },
          );
        } else {
          addToast({
            type: "error",
            title: t("recording.videoFinishError"),
            description: t("recording.videoFinishErrorDescription"),
          });
          setExibirSaindoComponent(true);
          setLoadingStopRecording(false);
        }
      } else {
        addToast({
          type: "error",
          title: t("recording.videoFinishError"),
          description: t("recording.videoFinishErrorDescription"),
        });
        setExibirSaindoComponent(true);
        setLoadingStopRecording(false);
      }
    }
  };

  function verificarEChamarlimiteDeTempo() {
    //Tempo ilimitado caso seja zero, solicitação Alexandre em 17/12/2024
    const showTimerRecord = globalConfigurations?.find(
      (config: { name: string }) => config.name === "ShowTimerRecord",
    );

    const shouldShowToast = showTimerRecord?.visible === true;

    if (timeToCount && Number(timeToCount) > 0 && shouldShowToast) {
      addToast({
        type: "warning",
        title: t("recording.demoVersion"),
        description: t("recording.demoTimeLimit", { minutes: timeToCount }),
        timeLimitToast: 15000,
      });
      setLoadingStopRecording(false);

      const milissegundos = Number(timeToCount) * 60000;

      const timeoutFinish = setTimeout(() => {
        stopVideoRecord();
        setShowModalConfirmation(false);
      }, milissegundos);

      const timeout30SecondsLeft = setTimeout(() => {
        addToast({
          type: "warning",
          title: t("common.warning"),
          description: t("recording.thirtySecondsLeft"),
          timeLimitToast: 15000,
        });
      }, milissegundos - 30000);

      timeoutsRef.current = [timeoutFinish, timeout30SecondsLeft];
    }
  }

  async function onClickBackButton() {
    try {
      setShowModalConfirmationSair(false);
      setExibirSaindoComponent(false);

      // finalizar a gravação antes de sair
      const { status } = await HearingService.deleteRoom(
        location.state?.videoRoomId,
        location.state.authToken,
        location.state.deviceToken,
      );

      if (!status) {
        return addToast({
          type: "warning",
          title: t("common.warning"),
          description: t("recording.roomCloseError"),
        });
      }

      setShowModalConfirmation(false);
      navigate(-1);
      goBack();
    } catch (error) {
      return addToast({
        type: "warning",
        title: t("common.warning"),
        description: t("recording.roomCloseError"),
      });
    }
  }

  async function requestPermission() {
    return permissions.camera && permissions.mic;
  }

  const handleIframeLoad = () => {
    const getIframeDoc = () => {
      const iframe: any = document.getElementById("unaIframe");
      try {
        return iframe?.contentWindow?.document ?? null;
      } catch {
        return null;
      }
    };

    const enableButtonsIfCallReady = (doc: Document) => {
      const hasCall =
        doc.getElementsByClassName("video-call-container").length > 0;
      const hasError =
        doc.getElementsByClassName("error-content__text").length > 0;
      if (hasCall && !hasError) {
        setDisabledButtons(false);
      }
    };

    const hideExtraFooterButtons = (doc: Document) => {
      const footerBar = doc.getElementsByClassName("video-call-footer-bar");
      if (footerBar.length === 0) return;
      const buttonsContainer = footerBar[0].getElementsByClassName(
        "video-call-footer-bar__buttons",
      );
      if (buttonsContainer.length === 0) return;
      const contentDiv = buttonsContainer[0].getElementsByTagName("div");
      if (contentDiv.length > 2)
        (contentDiv[2] as HTMLElement).style.display = "none";
    };

    const hideExitButton = (doc: Document) => {
      const botaoSair = doc.querySelector('div[id^="ember923"]') as HTMLElement;
      if (botaoSair) botaoSair.style.display = "none";
    };

    setTimeout(() => {
      const doc = getIframeDoc();
      if (!doc) return;
      enableButtonsIfCallReady(doc);
      hideExtraFooterButtons(doc);
      hideExitButton(doc);
    }, 10000);
  };

  const getUnaIframeBaseURL = () =>
    import.meta.env.DEV && window.location.hostname.includes("trycloudflare")
      ? import.meta.env.VITE_APP_CLOUDFLAREDUNABASEURL
      : import.meta.env.VITE_APP_BASEURL_SITE;

  return (
    <Container>
      <HeaderContainer>
        <ContainerDescription>
          <Title
            style={{
              paddingLeft: 20,
            }}
          >
            {t("audiences.singular")} {location.state?.procedureNumber}
          </Title>
          <Subtitle>{location.state?.procedureCustomerSourceName}</Subtitle>
        </ContainerDescription>
      </HeaderContainer>
      {create ? (
        <>
          <BodyContainer>
            {exibirSaindoComponent ? (
              SaindoComponent()
            ) : (
              <iframe
                id="unaIframe"
                onLoad={handleIframeLoad}
                style={{
                  width: "100%",
                  height: "calc(100vh - 220px)",
                }}
                src={`${getUnaIframeBaseURL()}/una/onlyVideoRoom?roomId=${
                  location.state?.urlModerator.split("=")[1]
                }&inviteUsers=false`}
                title="W3Schools Free Online Web Tutorials"
              ></iframe>
            )}
          </BodyContainer>
          <FooterContainer>
            <ContainerButtons>
              <input
                type="text"
                value={location.state?.urlAttendee}
                id="myInput"
                style={{ display: "none" }}
              ></input>

              {loadingStartRecording ? (
                <ReactLoading
                  type="spin"
                  color="#F2A60D"
                  width={24}
                  height={24}
                />
              ) : (
                <img
                  src={
                    recordStarted || disabledButtons
                      ? RecordButtonDisabled
                      : RecordButton
                  }
                  alt=""
                  onClick={() => {
                    if (!recordStarted || !disabledButtons) startVideoRecord();
                  }}
                  style={{
                    cursor:
                      recordStarted || disabledButtons
                        ? "not-allowed"
                        : "pointer",
                  }}
                />
              )}

              <ButtonFinish color="primary" onClick={copyFunction}>
                <img
                  src={Share}
                  alt=""
                  width={24}
                  height={24}
                  style={{ marginRight: 4 }}
                />
                Compartilhar link
              </ButtonFinish>
              {loadingStopRecording ? (
                <ReactLoading
                  type="spin"
                  color="#a50303"
                  width={24}
                  height={24}
                />
              ) : (
                <ButtonFinish
                  id="finish-record"
                  color="forth"
                  disabled={
                    !recordStarted ||
                    !(localStorage.getItem("isRecordStarted") === "true")
                  }
                  onClick={() => {
                    if (recordStarted) setShowModalConfirmation(true);
                  }}
                >
                  <img src={V} alt="" style={{ marginRight: 4 }} />
                  Finalizar gravação
                </ButtonFinish>
              )}
            </ContainerButtons>
          </FooterContainer>
        </>
      ) : (
        <div style={{ height: "100%", marginTop: "30px" }}>
          <BodyContainer>
            <SearchEmpty
              typeIcon="avatar"
              textMessage={t("common.noPermissionForProfile")}
            />
          </BodyContainer>
        </div>
      )}

      <ModalDefault
        openModal={showModalConfirmation}
        setOpenModal={setShowModalConfirmation}
        title={t("recording.finishRecording")}
        bodyMessageTitle={t("recording.confirmFinish")}
        bodyMessageSubTitle={t("common.actionCannotBeUndone")}
        textConfirm={t("common.finish").toUpperCase()}
        onClickConfirm={() => {
          stopVideoRecord();
          setShowModalConfirmation(false);
          goBack();
        }}
        textCancel={t("common.cancel").toUpperCase()}
        bodyMessageMarginBetweenTitleAndSubTitle="30px"
        bodyMessageMarginTop="30px"
        onClickCancel={() => {
          setShowModalConfirmation(false);
        }}
      />

      <ModalDefault
        openModal={showModalConfirmationSair}
        setOpenModal={setShowModalConfirmationSair}
        title={t("recording.leaveRoom")}
        bodyMessageTitle={t("recording.confirmLeaveRoom")}
        bodyMessageSubTitle={
          !recordStarted
            ? t("recording.clickFinishToLeave")
            : t("common.actionCannotBeUndone")
        }
        textConfirm={t("common.leave").toUpperCase()}
        onClickConfirm={onClickBackButton}
        textCancel={t("common.cancel").toUpperCase()}
        bodyMessageMarginBetweenTitleAndSubTitle="30px"
        bodyMessageMarginTop="30px"
        onClickCancel={() => {
          setShowModalConfirmationSair(false);
        }}
      />
    </Container>
  );
}

function SaindoComponent() {
  return (
    <Container
      style={{
        display: "flex",
        flexDirection: "row",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <Title
        style={{
          fontSize: 40,
          color: "black",
        }}
      >
        Saindo da sala...
      </Title>
    </Container>
  );
}
